import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import RouteManager from "../../managers/RouteManager";
import AppManager from "../../managers/AppManager";
import {
    DKLabel,
    DKIcon,
    DKLine,
    showToast,
    TOAST_TYPE,
    showAlert,
    DKTooltipWrapper
} from "deskera-ui-library";
import AddBankAccount from "./AddBankAccount";
import ic_add from "../../assets/icons/ic_add.png";
import ic_edit from "../../assets/icons/ic_edit.png";
import ic_delete from "../../assets/icons/ic_delete.png";
import BankAccountService from "../../services/BankAccountService";
import { removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import { TENANT, TenantService } from "../../managers/TenantManagerService";
import { COUNTRY } from "../../constants/CountryConstant";
import AddBankAccountUS from "../compliance/us/AddBankAccountUS";
import ProfileSetupService from "../../services/ProfileSetup-service";
import { PROFILE_STAGES_CONSTANT } from "../../constants/Constant";
import TenantManager from "../../managers/TenantManager";
import check from "../../assets/icons/check.svg";
import PermissionUtils, { ACTION, MODULE } from "../user-permissions/PermissionUtils";

function BankAccounts(props) {
    const history = useHistory();
    const { t } = useTranslation();

    const [bankAccounts, setBankAccounts] = useState([]);
    const [showAddBankPopup, setShowAddBankPopup] = useState(false);
    const [countryCode] = useState(TenantService.get(TENANT.COUNTRY_CODE));

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();

        getBankAccounts();
    }, []);

    const getBankAccounts = () => {        
        let accountType;
        switch (countryCode) {
            case COUNTRY.SG:
            case COUNTRY.MY:
                accountType = "GIRO";
                break;
            default:
                accountType = "BANK";
                break;
        }

        const timeout = showDelayedLoader();
        BankAccountService.getBankAccounts(accountType).then(
            (response) => {
                removeDelayedLoader(timeout);
                if (response?.bankAccounts?.length > 0) {
                    setBankAccounts(response.bankAccounts);
                    if (countryCode === COUNTRY.US) {
                        updateStateOfProfileStage();
                    }
                } else {
                    setBankAccounts([]);
                }
            }, (error) => {
                removeDelayedLoader(timeout);
                setBankAccounts([]);
            }
        );
    }

    const updateStateOfProfileStage = () => {
        ProfileSetupService.updateStateOfProfileStage(PROFILE_STAGES_CONSTANT.BANK_DETAILS).then(
            (response) => {
                TenantManager.setProfileData(response['profileStages']);
            }
        );
    }

    const onAddBankAccountPopupClose = (refresh) => {
        setShowAddBankPopup(false);
        if (refresh) {
            getBankAccounts();
        }
    }

    const showConfirmationMessage = (account) => {
        showAlert(t("ALERT"), t("DELETE_CONFIRM"), [
            {
                title: t("YES"),
                className: "bg-app text-white mr-xl",
                onClick: () => {
                    deleteBankAccount(account);
                }
            },
            {
                title: t("NO"),
                className: "bg-gray1 border-m"
            }
        ]);
    }

    const deleteBankAccount = (account) => {
        BankAccountService.deleteBankAccount(account.id).then(
            (response) => {
                showToast(t("BANK_DELETED_SUCCESS"), TOAST_TYPE.SUCCESS);
                getBankAccounts();
            }, (error) => {
                if (error?.errorMessage) {
                    showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                } else {
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            }
        );
    }

    const renderAddNewBankAccountCard = () => {
        return (
            <div className="card-col-3 p-l m-l add-new-bank-account-card">
                <DKIcon
                    src={ic_add}
                    className="ic-l cursor-hand"
                    onClick={() => {
                        setShowAddBankPopup(true)
                    }}
                />
                <DKLabel text={t("ADD_NEW")} className="fs-m fw-b mt-s" />
                <DKLabel text={t("ADD_NEW_TEXT")} className="fs-s text-gray mt-xs" />
            </div>
        );
    }

    const renderBankAccountCard = (account) => {
        return (
            <div className="card-col-3 p-l m-l bg-gray1 border-radius-m bank-account-card">
                <div className="row">
                    <DKLabel text={`${account.bank} ${account.bank === "DBS" ? "Bank (" + account.companyCode + ")" : ""}`} className="fs-l fw-b" />
                    {countryCode === COUNTRY.US && account.isVerified &&
                        <div className="ml-auto">
                            <DKTooltipWrapper content={t("PLAID_VERIFIED")} tooltipClassName="warning-tooltip-prop">
                                <DKIcon src={check} className="ic-s-2" />
                            </DKTooltipWrapper>
                        </div>
                    }
                </div>
                <DKLabel text={account.format} className="ic-s mt-xs" />
                <div className="d-flex justify-content-between mt-xl">
                    <div>
                        <DKLabel text={t("ACCOUNT_NUMBER")} className="ic-s" />
                        <DKLabel text={account.accountNumber} className="ic-s fw-b" />
                    </div>
                    <div>
                        <DKLabel text={countryCode === COUNTRY.US ? t("ROUTING_NUMBER") : t("BRANCH_CODE")} className="ic-s" />
                        <DKLabel text={countryCode === COUNTRY.US ? account.routingNumber : account.branchCode} className="ic-s fw-b" />
                    </div>
                </div>
                <div className="d-flex mt-m bank-account-card-actions">
                    <div className="d-flex ml-auto">
                        <DKTooltipWrapper content={t("EDIT_ACCOUNT")} tooltipClassName="warning-tooltip-prop">
                            <DKIcon
                                src={ic_edit}
                                className="ic-s-2 cursor-hand"
                                onClick={() => {
                                    BankAccountService.BANK_ACCOUNT = account;
                                    setShowAddBankPopup(true);
                                }}
                            />
                        </DKTooltipWrapper>
                        <DKTooltipWrapper content={t("DELETE_ACCOUNT")} tooltipClassName="warning-tooltip-prop">
                            <DKIcon
                                src={ic_delete}
                                className="ic-s-2 cursor-hand ml-l"
                                onClick={() => {
                                    showConfirmationMessage(account);
                                }}
                            />
                        </DKTooltipWrapper>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="parent-width" style={{
            pointerEvents: PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT]) ? "":"none",
            opacity: PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT]) ? "1":"0.9"
          }}>
            {showAddBankPopup && countryCode !== COUNTRY.US && <AddBankAccount onClose={(refresh) => {onAddBankAccountPopupClose(refresh)}} />}
            {showAddBankPopup && countryCode === COUNTRY.US && <AddBankAccountUS onClose={(refresh) => {onAddBankAccountPopupClose(refresh)}} />}
            <DKLabel text={t("BANK_ACCOUNTS")} className="fs-l fw-b" />
            <div className="bg-white border-radius-m m-v-l">
                <DKLabel text={t("BANK_ACCOUNTS")} className="fs-m fw-b p-h-xl pt-xl pb-s" />
                <DKLine className="bg-gray3" />
                <div className="card-row p-m">
                    {PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT]) && renderAddNewBankAccountCard()}
                    {bankAccounts.map((account) => renderBankAccountCard(account))}
                </div>
            </div>
        </div>
    );
}

export default BankAccounts;
